import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Briefcase, Award, FileText, Upload, Check } from 'lucide-react';
import { supabase } from '../supabaseClient';

export default function ProfileLawyer({ profileData, onSave, onNavigate, user }) {
    const [formData, setFormData] = useState(profileData || {});
    const [isEditing, setIsEditing] = useState(false); // Default to view mode
    const [saving, setSaving] = useState(false);
    const [isListedAdvocate, setIsListedAdvocate] = useState(Boolean(profileData?.is_listed_advocate));
    const [photoPreview, setPhotoPreview] = useState(
        profileData?.photo_path || null
    );

    // Update formData when profileData changes
    React.useEffect(() => {
        if (profileData) {
            setFormData(profileData);
            setIsListedAdvocate(Boolean(profileData.is_listed_advocate));
            if (profileData.photo_path) {
                setPhotoPreview(profileData.photo_path);
            }
        }
    }, [profileData]);

    React.useEffect(() => {
        if (!user?.id && !formData?.email) return;

        const checkListingStatus = async () => {
            try {
                let query = supabase.from('advocates').select('id').limit(1);
                if (user?.id) {
                    query = query.eq('user_id', user.id);
                } else {
                    query = query.eq('email', formData.email);
                }
                const { data } = await query.maybeSingle();
                if (data?.id) {
                    setIsListedAdvocate(true);
                    setFormData((prev) => ({ ...prev, is_listed_advocate: 1 }));
                }
            } catch (err) {
                console.warn('Could not verify advocate listing status:', err);
            }
        };

        checkListingStatus();
    }, [user?.id, formData?.email]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const filename = `user_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
            try {
                const { error: uploadError } = await supabase.storage
                    .from('user-photos')
                    .upload(filename, file, { contentType: file.type, upsert: true });

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('user-photos')
                    .getPublicUrl(filename);

                setPhotoPreview(publicUrl);
                setFormData(prev => ({ ...prev, photo_path: publicUrl }));
            } catch (err) {
                console.error("Error uploading photo:", err);
                alert("Failed to upload photo");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const result = await onSave(formData);
        setSaving(false);
        if (result.success) {
            setIsEditing(false);
        }
    };

    const getMissingRequiredFields = () => {
        const requiredChecks = [
            ['Full Name', formData.name],
            ['Phone', formData.phone],
            ['Bar License Number', formData.bar_license],
            ['Years of Experience', formData.experience !== null && formData.experience !== undefined && String(formData.experience).trim() !== '' && Number(formData.experience) > 0],
            ['At least one Specialization', Array.isArray(formData.specializations) && formData.specializations.length > 0],
            ['Address', formData.address],
            ['City', formData.city],
            ['State', formData.state],
            ['Zip Code', formData.zip],
            ['Bio', formData.bio],
            ['At least one Case History', Array.isArray(formData.case_histories) && formData.case_histories.length > 0],
        ];

        return requiredChecks
            .filter(([, value]) => !value)
            .map(([label]) => label);
    };

    const handleListAsAdvocate = async () => {
        try {
            if (isListedAdvocate || formData.is_listed_advocate) {
                return;
            }

            const missingFields = getMissingRequiredFields();
            if (missingFields.length > 0) {
                setIsEditing(true);
                alert(`Please complete required details before listing as advocate:\n\n${missingFields.join('\n')}`);
                return;
            }

            // Check if already listed in advocates table.
            // Prefer user_id for stronger matching; fallback to email.
            let existing = null;
            if (user?.id) {
                const { data } = await supabase
                    .from('advocates')
                    .select('id')
                    .eq('user_id', user.id)
                    .maybeSingle();
                existing = data;
            }
            if (!existing && formData.email) {
                const { data } = await supabase
                    .from('advocates')
                    .select('id')
                    .eq('email', formData.email)
                    .maybeSingle();
                existing = data;
            }

            if (existing) {
                // Update existing listing
                await supabase.from('advocates').update({
                    user_id: user?.id || null,
                    name: formData.name,
                    phone: formData.phone,
                    location: formData.city || '',
                    bar_number: formData.bar_license || '',
                    practice_areas: formData.specializations || [],
                    experience: formData.experience ? String(formData.experience) : '',
                    bio: formData.bio || '',
                    photo_path: formData.photo_path || null,
                    price_min: formData.price_min ? parseInt(formData.price_min) : null,
                    price_max: formData.price_max ? parseInt(formData.price_max) : null,
                }).eq('email', formData.email);
            } else {
                // Insert new listing
                await supabase.from('advocates').insert([{
                    user_id: user?.id || null,
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    location: formData.city || '',
                    bar_number: formData.bar_license || '',
                    practice_areas: formData.specializations || [],
                    experience: formData.experience ? String(formData.experience) : '',
                    bio: formData.bio || '',
                    photo_path: formData.photo_path || null,
                    price_min: formData.price_min ? parseInt(formData.price_min) : null,
                    price_max: formData.price_max ? parseInt(formData.price_max) : null,
                }]);
            }

            // Mark profile as listed
            await supabase.from('user_profiles')
                .update({ is_listed_advocate: 1 })
                .eq('id', user?.id);

            setIsListedAdvocate(true);
            setFormData((prev) => ({ ...prev, is_listed_advocate: 1 }));
            alert('Successfully listed as advocate! You are now visible in the Advocate List.');
        } catch (error) {
            console.error('List as advocate error:', error);
            alert('Error listing as advocate: ' + error.message);
        }
    };

    const specializations = [
        'Criminal Law', 'Civil Law', 'Corporate Law', 'Family Law',
        'Tax Law', 'Intellectual Property', 'Real Estate', 'Immigration'
    ];

    return (
        <div className="profile-page">
            <div className="profile-header">
                <button className="back-button" onClick={() => onNavigate('dashboard')}>
                    ← Back to Dashboard
                </button>
                <h1>Lawyer Profile</h1>
                {!isEditing && (
                    <button className="edit-button" onClick={() => setIsEditing(true)}>
                        Edit Profile
                    </button>
                )}
            </div>

            {!isEditing ? (
                /* VIEW MODE - Clean Display */
                <div className="profile-view-mode">
                    <div className="profile-view-header">
                        <div className="profile-view-photo">
                            {photoPreview ? (
                                <img src={photoPreview} alt="Profile" />
                            ) : (
                                <div className="profile-view-photo-placeholder">
                                    <User size={40} />
                                </div>
                            )}
                        </div>
                        <div className="profile-view-info">
                            <h2>{formData.name || 'No Name'}</h2>
                            <p>{formData.email}</p>
                        </div>
                    </div>

                    <div className="profile-view-grid">
                        <div className="profile-view-section">
                            <h3><Phone size={18} /> Contact Information</h3>
                            <div className="profile-view-field">
                                <div className="profile-view-label">Phone</div>
                                <div className="profile-view-value">{formData.phone || 'Not provided'}</div>
                            </div>
                            <div className="profile-view-field">
                                <div className="profile-view-label">Address</div>
                                <div className="profile-view-value">
                                    {formData.address ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}` : 'Not provided'}
                                </div>
                            </div>
                        </div>

                        <div className="profile-view-section">
                            <h3><Award size={18} /> Professional Details</h3>
                            <div className="profile-view-field">
                                <div className="profile-view-label">Bar License</div>
                                <div className="profile-view-value">{formData.bar_license || 'Not provided'}</div>
                            </div>
                            <div className="profile-view-field">
                                <div className="profile-view-label">Experience</div>
                                <div className="profile-view-value">{formData.experience || 0} years</div>
                            </div>
                        </div>
                    </div>

                    {formData.specializations && formData.specializations.length > 0 && (
                        <div className="profile-view-section">
                            <h3><Briefcase size={18} /> Specializations</h3>
                            <div className="profile-view-tags">
                                {formData.specializations.map(spec => (
                                    <span key={spec} className="profile-view-tag">{spec}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {formData.bio && (
                        <div className="profile-view-bio">
                            <h3>About</h3>
                            <p>{formData.bio}</p>
                        </div>
                    )}

                    {formData.case_histories && formData.case_histories.length > 0 && (
                        <div className="profile-view-bio" data-tour="Case History List or Timeline">
                            <h3>Case Histories</h3>
                            <p>{formData.case_histories.join('\n')}</p>
                        </div>
                    )}

                    <div className="profile-actions">
                        {(isListedAdvocate || formData.is_listed_advocate) ? (
                            <div className="listed-badge">
                                <Check size={16} /> Listed as Advocate
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="list-advocate-button"
                                onClick={handleListAsAdvocate}
                            >
                                List as Advocate
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                /* EDIT MODE - Form Display */
                <form onSubmit={handleSubmit} className="profile-form">
                    {/* Profile Photo Section */}
                    <div className="profile-section photo-section">
                        <div className="photo-upload-container">
                            <div className="photo-preview">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Profile" />
                                ) : (
                                    <div className="photo-placeholder">
                                        <User size={48} />
                                    </div>
                                )}
                            </div>
                            {isEditing && (
                                <label className="photo-upload-button">
                                    <Upload size={16} />
                                    Upload Photo
                                    <input type="file" accept="image/*" onChange={handlePhotoUpload} hidden />
                                </label>
                            )}
                        </div>
                    </div>

                    {/* General Information */}
                    <div className="profile-section">
                        <h2>General Information</h2>
                        <div className="form-grid">
                            <div className="form-field">
                                <label><User size={16} /> Full Name *</label>
                                <input
                                    type="text"
                                    value={formData.name || ''}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label><Mail size={16} /> Email</label>
                                <input type="email" value={formData.email || ''} disabled />
                            </div>
                            <div className="form-field">
                                <label><Phone size={16} /> Phone *</label>
                                <input
                                    type="tel"
                                    value={formData.phone || ''}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Professional Information */}
                    <div className="profile-section">
                        <h2>Professional Information</h2>
                        <div className="form-grid">
                            <div className="form-field">
                                <label><Award size={16} /> Bar License Number *</label>
                                <input
                                    type="text"
                                    value={formData.bar_license || ''}
                                    onChange={(e) => handleChange('bar_license', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label><Briefcase size={16} /> Years of Experience *</label>
                                <input
                                    type="number"
                                    value={formData.experience || 0}
                                    onChange={(e) => handleChange('experience', parseInt(e.target.value))}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-field specializations-field">
                            <label><FileText size={16} /> Specializations *</label>
                            <div className="specializations-grid">
                                {specializations.map(spec => (
                                    <label key={spec} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={(formData.specializations || []).includes(spec)}
                                            onChange={(e) => {
                                                const current = formData.specializations || [];
                                                const updated = e.target.checked
                                                    ? [...current, spec]
                                                    : current.filter(s => s !== spec);
                                                handleChange('specializations', updated);
                                            }}
                                            disabled={!isEditing}
                                        />
                                        {spec}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="profile-section">
                        <h2>Location</h2>
                        <div className="form-grid">
                            <div className="form-field full-width">
                                <label><MapPin size={16} /> Address *</label>
                                <input
                                    type="text"
                                    value={formData.address || ''}
                                    onChange={(e) => handleChange('address', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label>City *</label>
                                <input
                                    type="text"
                                    value={formData.city || ''}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label>State *</label>
                                <input
                                    type="text"
                                    value={formData.state || ''}
                                    onChange={(e) => handleChange('state', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label>Zip Code *</label>
                                <input
                                    type="text"
                                    value={formData.zip || ''}
                                    onChange={(e) => handleChange('zip', e.target.value)}
                                    disabled={!isEditing}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label>Min Fee (PKR)</label>
                                <input
                                    type="number"
                                    value={formData.price_min || ''}
                                    onChange={(e) => handleChange('price_min', e.target.value)}
                                    disabled={!isEditing}
                                    placeholder="e.g. 10000"
                                />
                            </div>
                            <div className="form-field">
                                <label>Max Fee (PKR)</label>
                                <input
                                    type="number"
                                    value={formData.price_max || ''}
                                    onChange={(e) => handleChange('price_max', e.target.value)}
                                    disabled={!isEditing}
                                    placeholder="e.g. 50000"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bio */}
                    <div className="profile-section">
                        <h2>About / Bio *</h2>
                        <div className="form-field full-width">
                            <textarea
                                value={formData.bio || ''}
                                onChange={(e) => handleChange('bio', e.target.value)}
                                disabled={!isEditing}
                                placeholder="Tell clients about your experience, expertise, and approach..."
                                rows={6}
                                maxLength={500}
                                required
                            />
                            <small>{(formData.bio || '').length}/500 characters</small>
                        </div>
                    </div>

                    {/* Case Histories */}
                    <div className="profile-section">
                        <h2>Case Histories *</h2>
                        <div className="form-field full-width">
                            <textarea
                                value={(formData.case_histories || []).join('\n')}
                                onChange={(e) => handleChange('case_histories', e.target.value.split('\n').filter(Boolean))}
                                disabled={!isEditing}
                                placeholder="List notable cases (one per line)..."
                                rows={4}
                                required
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="profile-actions">
                        {isEditing ? (
                            <>
                                <button type="submit" className="save-button" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    type="button"
                                    className="cancel-button"
                                    onClick={() => {
                                        setFormData(profileData || {});
                                        if (profileData?.photo_path) {
                                            setPhotoPreview(profileData.photo_path);
                                        } else {
                                            setPhotoPreview(null);
                                        }
                                        setIsEditing(false);
                                    }}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                {(isListedAdvocate || formData.is_listed_advocate) ? (
                                    <div className="listed-badge">
                                        <Check size={16} /> Listed as Advocate
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="list-advocate-button"
                                        onClick={handleListAsAdvocate}
                                    >
                                        List as Advocate
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </form>
            )}
        </div>
    );
}